// src/pages/Tasks/TaskEdit.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { tasksApi, Task, TaskUpdatePayload, TaskType } from '../../api/tasks';
import { clientsApi, Client } from '../../api/clients';
import { vendorsApi, Vendor } from '../../api/vendors';
import { projectsApi, Project } from '../../api/projects';
import { getAllUsers } from '../../api/user';
import { usePermissionGuard } from '../../hooks/usePermissionGuard';
import { useFileDropPaste } from '../../hooks/useFileDropPaste';
import { format } from "date-fns";
import { createPortal } from 'react-dom';
// AI helpers
import AiGenerateButton from '../../components/AiGenerateButton';
import ErrorBoundary from '../../components/ErrorBoundary';
import { generateTaskChecklist, generateTaskDescription } from '../../api/taskAi';
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import { AttachmentThumb } from "../../components/AttachmentThumb";
import TaskWorkloadPreviewList from "../../components/TaskWorkloadPreviewList";
import { EmployeeMultiSelect } from "../../components/ui/EmployeeMultiSelect";
import { EntityMultiSelect } from "../../components/ui/EntityMultiSelect";
import { AutoGrowTextarea } from "../../components/AutoGrowTextarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { buildRecurrence, computeNextSpawnAt, intervalUnit, type RecurrenceInterval } from '../../utils/recurrence';
import { toast } from "sonner";
import { shouldHideAssignmentControls } from '../../utils/crossOrgTaskPermissions';
import { useTaskWorkloadPreviews } from "../../hooks/useTaskWorkloadPreviews";
import PersonAvatar from "../../components/people/PersonAvatar";
import {
  combineHoursAndMinutes,
  formatTaskApiError,
  getTaskCapacityBlockers,
  parseTaskHours,
  resolveTaskCapacityDate,
  resolveTaskEffectiveHours,
  splitHoursAndMinutes,
  taskStatusConsumesCapacity,
} from "../../utils/taskWorkloadCapacity";

interface TaskEditProps {
  isOpen: boolean;
  task: Task | null;
  /**
   * Full detail payload from `GET /tasks/{id}` — includes the task object
   * plus `assignees` and `checklist`. Passed down from TaskEditPage so the
   * dialog doesn't have to fetch the detail a second time (the second
   * fetch was failing silently in some routes, leaving the form blank).
   */
  taskDetail?: any;
  onClose: () => void;
  onSuccess?: () => void;
}

interface User {
  id: string;
  user_id?: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
  department_id?: string;
  employee_id?: string;
}

interface ApprovalLevel {
  level: number;
  approver: string;
  roleLabel: string;
  status: string;
  actionAt: string | null;
  remark: string;
}

interface TaskTypeWithDefault extends TaskType {
  is_default?: boolean;
}

type EditFormData = TaskUpdatePayload & { approvalChain?: ApprovalLevel[]; recurrence_interval?: RecurrenceInterval; recurrence_every?: number };

function buildFormFromTask(task: Task): EditFormData {
  return {
    title: task.title || '',
    description: task.description || '',
    task_type: task.task_type || 'GENERAL',
    tags: task.tags || [],
    priority: (task.priority as any) || 'MEDIUM',
    department_id: task.department_id || '',
    start_date: task.start_date ? task.start_date.split('T')[0] : '',
    due_date: task.due_date ? task.due_date.slice(0, 16) : '',
    estimated_hours: task.estimated_hours ?? undefined,
    actual_hours: task.actual_hours ?? undefined,
    progress_percent: task.progress_percent ?? undefined,
    client_id: task.client_id ?? null,
    vendor_id: task.vendor_id ?? null,
    project_id: task.project_id ?? null,
    // Multi-select lists; a task saved before multi-select carries only the
    // singular id, which getEntitySelection folds in when reading these.
    client_ids: Array.isArray((task as any).client_ids) ? (task as any).client_ids : [],
    vendor_ids: Array.isArray((task as any).vendor_ids) ? (task as any).vendor_ids : [],
    project_ids: Array.isArray((task as any).project_ids) ? (task as any).project_ids : [],
    is_recurring: !!task.is_recurring,
    recurrence_interval: ((task.recurrence_rule && task.recurrence_rule.interval) as RecurrenceInterval) || 'WEEKLY',
    recurrence_every: (task.recurrence_rule && task.recurrence_rule.every) || 1,
    assignees: [],
    checklist: [],
    replace_checklist: true,
    replace_assignees: true,
    approvalChain: [],
  };
}


const compactUniqueIds = (ids: Array<string | null | undefined>): string[] =>
  Array.from(new Set(ids.filter((id): id is string => !!id)));

// Read one entity link out of the form as a list. Tasks saved before
// multi-select carry only the singular id, so fold it in rather than showing
// an empty picker; the first id stays the primary link the backend persists.
const getEntitySelection = (
  form: Record<string, any>,
  multiKey: 'client_ids' | 'vendor_ids' | 'project_ids',
  legacyKey: 'client_id' | 'vendor_id' | 'project_id',
): string[] => {
  const multi = Array.isArray(form[multiKey]) ? form[multiKey] : [];
  return compactUniqueIds([...(multi as Array<string | null | undefined>), form[legacyKey]]);
};

function TaskEdit({ isOpen, task, taskDetail, onClose, onSuccess }: TaskEditProps) {
  const { showError, showSuccess } = usePermissionGuard();
  const hideAssignmentControls = shouldHideAssignmentControls(
    taskDetail?.is_originator_view ? taskDetail : task,
  );
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<'basic' | 'details' | 'approval' | 'checklist' | 'attachments'>('basic');
  // Attachments live on the task already, so we operate against the existing id.
  const [attachments, setAttachments] = useState<Array<{ id: string; file_name: string; file_url?: string | null; file_size?: number; mime_type?: string }>>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  // Reference to the edit form so the persistent "Save Changes" button in
  // the modal header can trigger submission from any tab without the user
  // having to navigate to the Attachments tab first.
  const formRef = useRef<HTMLFormElement>(null);
  // Seeds the default reviewer (the task creator) at most once per open, so it
  // never re-appears after the user deliberately clears all reviewers.
  const defaultReviewerSeededRef = useRef(false);
  const triggerSave = () => {
    // requestSubmit() runs the form's onSubmit handler (handleSubmit) and
    // respects native form validation. Fall back to a synthetic submit for
    // very old browsers that don't have it.
    const form = formRef.current;
    if (!form) return;
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
  };

  const [taskTypes, setTaskTypes] = useState<TaskTypeWithDefault[]>([]);
  const [loadingTaskTypes, setLoadingTaskTypes] = useState(false);
  const [showCreateTaskTypeModal, setShowCreateTaskTypeModal] = useState(false);
  const [newTaskTypeName, setNewTaskTypeName] = useState('');
  const [creatingTaskType, setCreatingTaskType] = useState(false);
  const [editingType, setEditingType] = useState<TaskTypeWithDefault | null>(null);
  const [editName, setEditName] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteType, setDeleteType] = useState<TaskTypeWithDefault | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [clients, setClients] = useState<Client[]>([]);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ clientName: '', email: '', phone: '', address1: '', address2: '', state: '', country: '', pincode: '', gstin: '', pan: '' });
  const [savingClient, setSavingClient] = useState(false);

  const [vendors, setVendors] = useState<Vendor[]>([]);

  const [projects, setProjects] = useState<Project[]>([]);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({ name: '', description: '', client_id: '' });
  const [savingProject, setSavingProject] = useState(false);

  const [selectedPreparors, setSelectedPreparors] = useState<string[]>([]);
  const [checklistItems, setChecklistItems] = useState<string[]>([]);

  // Due date states
  const [dueOpen, setDueOpen] = useState(false);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [dueTime, setDueTime] = useState<string>("");

  const [formData, setFormData] = useState<EditFormData>(
    task ? buildFormFromTask(task) : { title: '', priority: 'MEDIUM', assignees: [], checklist: [] }
  );
  const selectedClientIds = getEntitySelection(formData as any, 'client_ids', 'client_id');
  const selectedVendorIds = getEntitySelection(formData as any, 'vendor_ids', 'vendor_id');
  const selectedProjectIds = getEntitySelection(formData as any, 'project_ids', 'project_id');

  // Load the task's existing attachments so the Attachments tab can list them.
  const loadAttachments = useCallback(async () => {
    if (!task?.id) return;
    setLoadingAttachments(true);
    try {
      const res = await tasksApi.getAttachments(task.id);
      const data = res?.data;
      const list = Array.isArray(data) ? data : (data?.attachments || []);
      setAttachments(list);
    } catch {
      // Non-fatal — leave attachments empty.
    } finally {
      setLoadingAttachments(false);
    }
  }, [task?.id]);

  // Shared by the file picker onChange, drag-drop, and Ctrl/Cmd+V paste.
  const uploadAttachmentFiles = useCallback(async (files: File[]) => {
    if (!files.length || !task?.id) return;
    const tooBig = files.filter(f => f.size > ATTACHMENT_MAX_BYTES);
    const ok = files.filter(f => f.size <= ATTACHMENT_MAX_BYTES);
    if (tooBig.length > 0) {
      showError('UPLOAD_FAILED', `${tooBig.map(f => f.name).join(', ')} exceeds the 5 MB limit and was skipped.`);
    }
    if (ok.length === 0) return;
    setUploadingAttachments(true);
    const failed: string[] = [];
    for (const file of ok) {
      try {
        await tasksApi.uploadAttachment(task.id, file);
      } catch (err: any) {
        failed.push(`${file.name} (${err?.response?.data?.detail || err?.message || 'upload failed'})`);
      }
    }
    setUploadingAttachments(false);
    await loadAttachments();
    if (failed.length > 0) {
      showError('UPLOAD_FAILED', failed.join(', '));
    } else {
      showSuccess('Attachments uploaded', `${ok.length} file${ok.length === 1 ? '' : 's'} added.`);
    }
  }, [task?.id, ATTACHMENT_MAX_BYTES, showError, showSuccess, loadAttachments]);

  const { dropRef: attachmentDropRef, isDragOver: attachmentDragOver } = useFileDropPaste({
    onFiles: uploadAttachmentFiles,
    disabled: activeSection !== 'attachments' || uploadingAttachments,
    pasteOnDocument: true,
  });

  useEffect(() => {
    if (!isOpen || !task) return;

    defaultReviewerSeededRef.current = false;
    setFormData(buildFormFromTask(task));
    setSelectedPreparors([]);
    setChecklistItems([]);
    setActiveSection('basic');
    setAttachments([]);
    loadAttachments();

    // Initialize due date from task
    if (task.due_date) {
      const dueDateObj = new Date(task.due_date);
      setDueDate(dueDateObj);
      setDueTime(format(dueDateObj, "HH:mm"));
    } else {
      setDueDate(null);
      setDueTime("");
    }

    fetchTaskTypes();
    fetchUsers();
    clientsApi.list({ page_size: 200 }).then(r => setClients(r.data?.items || [])).catch(() => {});
    vendorsApi.list({ page_size: 200 }).then(r => setVendors(r.data?.items || [])).catch(() => {});
    projectsApi.list({ page_size: 200 }).then(r => setProjects(r.data?.items || [])).catch(() => {});

    const populate = (detail: any) => {
      const preparors: string[] = (detail?.assignees || [])
        .filter((a: any) => a.role_in_task === 'PREPAROR')
        .map((a: any) => a.user_id);
      setSelectedPreparors(preparors.length > 0 ? preparors : []);

      const reviewers: ApprovalLevel[] = (detail?.assignees || [])
        .filter((a: any) => a.role_in_task === 'REVIEWER')
        .map((a: any, idx: number) => ({
          level: idx + 1,
          approver: a.user_id,
          roleLabel: 'Reviewer',
          status: 'Pending',
          actionAt: null,
          remark: '',
        }));
      setFormData(prev => ({
        ...prev,
        approvalChain: reviewers.length > 0
          ? reviewers
          : [{ level: 1, approver: '', roleLabel: 'Reviewer', status: 'Pending', actionAt: null, remark: '' }],
      }));

      const items: string[] = (detail?.checklist || []).map((i: any) => i.item);
      setChecklistItems(items.length > 0 ? items : []);
    };

    // Prefer the detail handed down by TaskEditPage. Only fall back to a
    // fresh API fetch if the prop is missing (e.g. used as a modal in TaskCard).
    if (taskDetail && (taskDetail.assignees || taskDetail.checklist)) {
      populate(taskDetail);
    } else {
      tasksApi
        .get(task.id)
        .then((res: any) => populate(res?.data))
        .catch((err) => {
          console.error('TaskEdit: failed to load detail', err);
          setFormData(prev => ({
            ...prev,
            approvalChain: [
              { level: 1, approver: '', roleLabel: 'Reviewer', status: 'Pending', actionAt: null, remark: '' },
            ],
          }));
        });
    }
  }, [isOpen, task?.id, taskDetail]);

  // Once users have loaded, default the first reviewer to the TASK CREATOR —
  // but only while the approval chain is still the untouched empty default, so
  // existing reviewers and the user's own picks are never overwritten. The ref
  // keeps this to a single seed per open.
  useEffect(() => {
    if (!isOpen || defaultReviewerSeededRef.current) return;
    if (loadingUsers || users.length === 0) return;
    const creatorId = (task as any)?.created_by || '';
    if (!creatorId) return;
    setFormData(prev => {
      const chain = prev.approvalChain || [];
      const untouched = chain.length <= 1 && !chain[0]?.approver;
      if (!untouched) return prev;
      defaultReviewerSeededRef.current = true;
      return {
        ...prev,
        approvalChain: [{
          level: 1,
          approver: creatorId,
          roleLabel: 'Reviewer 1',
          status: 'Pending',
          actionAt: null,
          remark: '',
        }],
      };
    });
  }, [isOpen, users, loadingUsers, task]);

  const fetchTaskTypes = async () => {
    setLoadingTaskTypes(true);
    try {
      const response = await tasksApi.getTaskTypes();
      const taskTypesData = Array.isArray(response.data?.items) ? response.data.items : [];
      setTaskTypes(taskTypesData);
    } catch {
      setTaskTypes([]);
    } finally {
      setLoadingTaskTypes(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await getAllUsers();
      setUsers(response?.users || []);
    } catch {
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseFloat(value) : undefined) : value,
    }));
  };

  const setEstimatedWorkload = (hours: string, minutes: string) => {
    setFormData((prev) => ({
      ...prev,
      estimated_hours: combineHoursAndMinutes(hours, minutes),
    }));
  };

  const handleTaskTypeChange = (value: string) => {
    if (value === '__CREATE_NEW__') {
      setShowCreateTaskTypeModal(true);
    } else {
      setFormData(prev => ({ ...prev, task_type: value }));
    }
  };

  const handleEditType = (type: TaskTypeWithDefault) => {
    setEditingType(type);
    setEditName(type.name);
    setShowEditModal(true);
  };

  const handleDeleteType = (type: TaskTypeWithDefault) => {
    setDeleteType(type);
    setShowDeleteModal(true);
  };

  const handleUpdateTaskType = async () => {
    if (!editingType || !editName.trim()) {
      toast.error('Please enter a task type name');
      return;
    }

    setCreatingTaskType(true);
    try {
      await tasksApi.updateTaskType(editingType.id, { name: editName.trim() });
      await fetchTaskTypes();
      setShowEditModal(false);
      setEditingType(null);
      setEditName("");
      toast.success('Task type updated successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update task type');
    } finally {
      setCreatingTaskType(false);
    }
  };

  const handleDeleteTaskType = async () => {
    if (!deleteType) return;

    setLoading(true);
    try {
      await tasksApi.deleteTaskType(deleteType.id);
      await fetchTaskTypes();

      if (formData.task_type === deleteType.name) {
        setFormData({ ...formData, task_type: '' });
      }

      setShowDeleteModal(false);
      setDeleteType(null);
      toast.success('Task type deleted successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to delete task type');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTaskType = async () => {
    if (!newTaskTypeName.trim()) return;
    setCreatingTaskType(true);
    try {
      const response = await tasksApi.createTaskType({ name: newTaskTypeName });
      const newType = response.data;
      setTaskTypes(prev => [...(Array.isArray(prev) ? prev : []), newType]);
      setFormData(prev => ({ ...prev, task_type: newType.name }));
      setShowCreateTaskTypeModal(false);
      setNewTaskTypeName('');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create task type');
    } finally {
      setCreatingTaskType(false);
    }
  };

  // Reviewers are picked via a single multi-select. Each checked person becomes
  // an approval level in the order picked; role labels are auto-named. Existing
  // per-approver status/remark is preserved when the selection changes.
  const setReviewers = (ids: string[]) => {
    setFormData(prev => {
      const prevChain = prev.approvalChain || [];
      const chain: ApprovalLevel[] = ids.filter(Boolean).map((id, i) => {
        const existing = prevChain.find(l => l.approver === id);
        return {
          level: i + 1,
          approver: id,
          roleLabel: `Reviewer ${i + 1}`,
          status: existing?.status || 'Pending',
          actionAt: existing?.actionAt ?? null,
          remark: existing?.remark || '',
        };
      });
      return { ...prev, approvalChain: chain };
    });
  };

  const handleChecklistChange = useCallback((index: number, value: string) => {
    setChecklistItems(prev => { const n = [...prev]; n[index] = value; return n; });
  }, []);

  const addChecklistItem = useCallback(() => {
    setChecklistItems(prev => [...prev, '']);
  }, []);

  const removeChecklistItem = useCallback((index: number) => {
    setChecklistItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Due date handlers
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;

    const updated = new Date(date);

    if (dueTime) {
      const [h, m] = dueTime.split(":");
      updated.setHours(Number(h));
      updated.setMinutes(Number(m));
    } else {
      updated.setHours(18);
      updated.setMinutes(0);
    }

    setDueDate(updated);
    setFormData((prev) => ({
      ...prev,
      due_date: formatLocalDateTime(updated),
    }));
    setDueOpen(false);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value;
    setDueTime(time);

    if (dueDate) {
      const [hours, minutes] = time.split(':');
      const updated = new Date(dueDate);
      updated.setHours(parseInt(hours), parseInt(minutes));
      setDueDate(updated);
      setFormData((prev) => ({
        ...prev,
        due_date: formatLocalDateTime(updated),
      }));
    }
  };

  const formatLocalDateTime = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const handleSaveNewClient = async () => {
    if (!newClientForm.clientName.trim()) return;
    setSavingClient(true);
    try {
      const r = await clientsApi.create({
        clientName: newClientForm.clientName.trim(),
        email: newClientForm.email || undefined,
        phone: newClientForm.phone || undefined,
        address1: newClientForm.address1 || undefined,
        address2: newClientForm.address2 || undefined,
        state: newClientForm.state || undefined,
        country: newClientForm.country || undefined,
        pincode: newClientForm.pincode || undefined,
        gstin: newClientForm.gstin || undefined,
        pan: newClientForm.pan || undefined,
      });
      const created = r.data?.client;
      if (created) {
        setClients(prev => [...prev, created]);
        setFormData(prev => ({ ...prev, client_id: created.id }));
      }
      setNewClientForm({ clientName: '', email: '', phone: '', address1: '', address2: '', state: '', country: '', pincode: '', gstin: '', pan: '' });
      setShowAddClientModal(false);
    } catch {
    } finally {
      setSavingClient(false);
    }
  };

  const handleSaveNewProject = async () => {
    if (!newProjectForm.name.trim()) return;
    setSavingProject(true);
    try {
      const r = await projectsApi.create({
        name: newProjectForm.name.trim(),
        description: newProjectForm.description || undefined,
        client_id: newProjectForm.client_id || null,
      });
      const created = r.data?.project;
      if (created) {
        setProjects(prev => [...prev, created]);
        setFormData(prev => ({
          ...prev,
          project_id: created.id,
          project_ids: compactUniqueIds([
            ...getEntitySelection(prev as any, 'project_ids', 'project_id'),
            created.id,
          ]),
          ...(created.client_id ? {
            client_id: created.client_id,
            client_ids: compactUniqueIds([
              ...getEntitySelection(prev as any, 'client_ids', 'client_id'),
              created.client_id,
            ]),
          } : {}),
        } as any));
      }
      setNewProjectForm({ name: '', description: '', client_id: '' });
      setShowAddProjectModal(false);
    } catch {
    } finally {
      setSavingProject(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!task) return;
    if (hideAssignmentControls) {
      showError(
        'CANNOT_MODIFY_OTHERS_TASK',
        'This cross-organization draft is managed by the receiving organization.',
      );
      return;
    }
    if (hasCapacityBlockers) {
      showError('CANNOT_MODIFY_OTHERS_TASK', capacityBlockers[0]);
      return;
    }

    setLoading(true);

    const assignees: Array<{ user_id: string; role_in_task: 'PREPAROR' | 'REVIEWER' }> = [];

    selectedPreparors.forEach(preparorId => {
      if (preparorId) {
        assignees.push({ user_id: preparorId, role_in_task: 'PREPAROR' });
      }
    });

    (formData.approvalChain || []).forEach(level => {
      if (level.approver) {
        assignees.push({ user_id: level.approver, role_in_task: 'REVIEWER' });
      }
    });

    const filteredChecklist = checklistItems.filter(item => item.trim());

    const recurrence = buildRecurrence(formData);
    const payload: TaskUpdatePayload = {
      title: formData.title || undefined,
      description: formData.description || undefined,
      task_type: formData.task_type || undefined,
      tags: (formData.tags || []).filter(t => t.trim()),
      priority: formData.priority,
      department_id: formData.department_id || undefined,
      start_date: formData.start_date || undefined,
      due_date: formData.due_date || undefined,
      estimated_hours: parseTaskHours(formData.estimated_hours),
      actual_hours: parseTaskHours(formData.actual_hours),
      progress_percent: formData.progress_percent,
      // Plural fields carry the whole selection; the backend re-derives each
      // singular primary link from the first id.
      client_id: selectedClientIds[0] ?? null,
      client_ids: selectedClientIds,
      vendor_id: selectedVendorIds[0] ?? null,
      vendor_ids: selectedVendorIds,
      project_id: selectedProjectIds[0] ?? null,
      project_ids: selectedProjectIds,
      assignees,
      replace_assignees: true,
      checklist: filteredChecklist,
      replace_checklist: true,
      is_recurring: recurrence.is_recurring,
      recurrence_rule: recurrence.recurrence_rule, // null when toggled off → backend clears it
    };

    try {
      await tasksApi.update(task.id, payload);
      showSuccess('Task Updated', 'Task has been updated successfully');
      onClose();
      onSuccess?.();
    } catch (err: any) {
      showError('CANNOT_MODIFY_OTHERS_TASK', formatTaskApiError(err, 'Failed to update task'));
    } finally {
      setLoading(false);
    }
  };

  const isBasicValid = (formData.title || '').trim().length > 0;

  // Name only — used for the selected-person chips and preview, where a
  // department/id suffix would just be noise.
  const getUserDisplayName = useCallback((userId: string) => {
    const user = users.find(u => u.id === userId || u.user_id === userId);
    return user?.name || '-- Select --';
  }, [users]);

  // Exclude the task owner (creator) from the assignee pickers — they're the
  // owner, not a selectable preparor/reviewer. They can still be the default
  // reviewer (seeded above); it just isn't re-pickable from the list.
  const ownerId = (task as any)?.created_by || '';
  const assignableUsers = users.filter(
    (u) => (u.id || u.user_id || '') !== ownerId,
  );
  const capacityEmployeeIds = React.useMemo(
    () => Array.from(new Set(selectedPreparors.filter(Boolean))),
    [selectedPreparors],
  );
  const capacityDate = resolveTaskCapacityDate({
    startDate: formData.start_date || null,
    dueDate: formData.due_date || null,
  });
  const estimatedWorkloadParts = splitHoursAndMinutes(formData.estimated_hours);
  const requestedWorkloadHours = resolveTaskEffectiveHours(
    formData.estimated_hours,
    formData.actual_hours,
  );
  const workloadPreviews = useTaskWorkloadPreviews({
    enabled:
      isOpen &&
      !!task?.id &&
      capacityEmployeeIds.length > 0 &&
      taskStatusConsumesCapacity(task?.status),
    employeeIds: capacityEmployeeIds,
    capacityDate,
    excludeTaskId: task?.id,
  });
  const capacityBlockers = React.useMemo(
    () =>
      capacityEmployeeIds.length > 0 && taskStatusConsumesCapacity(task?.status)
        ? getTaskCapacityBlockers({
            employeeIds: capacityEmployeeIds,
            previews: workloadPreviews,
            requestedHours: requestedWorkloadHours,
            getEmployeeName: getUserDisplayName,
          })
        : [],
    [
      capacityEmployeeIds,
      getUserDisplayName,
      requestedWorkloadHours,
      task?.status,
      workloadPreviews,
    ],
  );
  const hasCapacityBlockers = capacityBlockers.length > 0;

  if (!isOpen || !task) return null;

  return createPortal(
    <div className="gt-themed">
      <div
        className="fixed inset-0 transition-opacity duration-300"
        style={{ background: 'rgba(15, 29, 20, 0.32)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', zIndex: 100 }}
        onClick={onClose}
      />

      <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 101 }}>
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="relative w-full max-w-4xl max-h-[94vh] overflow-hidden gt-modal flex flex-col animate-in fade-in zoom-in duration-300"
            onClick={e => e.stopPropagation()}
          >
            <div className="shrink-0 px-4 md:px-6 py-3 md:py-5"
              style={{
                background: 'linear-gradient(135deg, var(--gt-primary-container) 0%, var(--gt-surface-1) 100%)',
                borderBottom: '1px solid var(--gt-divider)',
              }}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="gt-leaf" style={{ width: 10, height: 10 }} />
                  </div>
                  <h2 className="gt-display">
                    <span className="text-[20px] md:text-[26px]">Edit <em className="gt-serif-italic" style={{ color: 'var(--gt-accent-strong)' }}>Task</em></span>
                  </h2>
                  <p className="gt-page-sub hidden md:block" style={{ marginTop: 4 }}>Update <em className="gt-serif-italic">task</em> details.</p>
                </div>
                {/* Save Changes lives in the header so the user can commit
                    edits from any tab — no need to walk all the way to
                    Attachments. It mirrors the in-form submit button:
                    `triggerSave()` calls `form.requestSubmit()`, which runs
                    the same `handleSubmit` path. Disabled while a save is
                    in flight or required fields are missing. */}
                <div className="flex items-center gap-2 shrink-0">
                  {!hideAssignmentControls && <button
                    type="button"
                    onClick={triggerSave}
                    disabled={loading || !isBasicValid || hasCapacityBlockers}
                    className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-1.5 shadow-md"
                    title={!isBasicValid ? 'Fill required fields in Basic first' : undefined}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Saving…
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save Changes
                      </>
                    )}
                  </button>}
                  {hideAssignmentControls && (
                    <p className="text-sm text-gray-600 max-w-xs text-right">
                      The receiving organization manages preparers and approvals for this draft.
                    </p>
                  )}
                  <button onClick={onClose} className="gt-icon-btn" aria-label="Close">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Step Indicator — single-line horizontal scroll on mobile */}
            <div className="shrink-0 px-4 md:px-6 pt-2 md:pt-3" style={{ borderBottom: '1px solid var(--gt-border)' }}>
              <nav className="flex gap-1 overflow-x-auto flex-nowrap gt-task-tabs">
                {(['basic', 'details', 'approval', 'checklist', 'attachments'] as const)
                  .filter((section) => !hideAssignmentControls || section !== 'approval')
                  .map((section) => {
                  const isActive = activeSection === section;
                  return (
                    <button
                      key={section}
                      type="button"
                      onClick={() => setActiveSection(section)}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = 'var(--gt-primary-deep)';
                          e.currentTarget.style.background = 'var(--gt-primary-container)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = 'var(--gt-text-tertiary)';
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                      className="transition-all"
                      style={{
                        padding: '10px 18px',
                        fontFamily: 'var(--gt-font-display)',
                        fontSize: 15,
                        fontWeight: 600,
                        letterSpacing: '0.01em',
                        borderTopLeftRadius: 10,
                        borderTopRightRadius: 10,
                        border: 'none',
                        borderBottom: isActive ? '2px solid var(--gt-primary)' : '2px solid transparent',
                        background: isActive ? 'var(--gt-surface-0)' : 'transparent',
                        color: isActive ? 'var(--gt-primary-deep)' : 'var(--gt-text-tertiary)',
                        boxShadow: isActive ? 'var(--gt-elev-1)' : 'none',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        marginBottom: -1,
                      }}
                    >
                      {/* "Approval Chain" is too wide for phones — short on mobile, full on md+ */}
                      {section === 'approval' ? (
                        <>
                          <span className="md:hidden">Approval</span>
                          <span className="hidden md:inline">Approval Chain</span>
                        </>
                      ) : (
                        section.charAt(0).toUpperCase() + section.slice(1)
                      )}
                    </button>
                  );
                  })}
              </nav>
            </div>

            {/* Form Body — flex-1 so it fills the rest of the modal and each
                section's action buttons stay visible at the bottom. */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 md:p-5 pb-[env(safe-area-inset-bottom,16px)]">
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">

                {/* Basic Section */}
                {activeSection === 'basic' && (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    {/* Row 1: Task ID + Task Title side by side so the form
                        fits without scrolling. */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-1">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Task ID
                          <span className="ml-1 text-xs font-normal text-gray-400">(read-only)</span>
                        </label>
                        <input
                          type="text"
                          value={task?.code || task?.id || ''}
                          readOnly
                          disabled
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-600 font-mono cursor-not-allowed select-all"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <div className="flex justify-between items-end mb-1.5 gap-3">
                          <label className="block text-sm font-semibold text-gray-700">
                            Task Title <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs text-gray-400">{(formData.title || '').length}/500</p>
                        </div>
                        <input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          required
                          maxLength={500}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-end mb-1.5 gap-3">
                        <label className="block text-sm font-semibold text-gray-700">Description</label>
                        <ErrorBoundary inline name="ai-generate-description">
                          <AiGenerateButton
                            idleLabel={formData.description ? 'Regenerate with AI' : 'Generate with AI'}
                            disabled={!(formData.title || '').trim()}
                            disabledHint="Enter a title first"
                            run={async () => {
                              const r = await generateTaskDescription(formData.title || '', formData.task_type);
                              if (!r.ok) return r.message || 'AI is unavailable right now.';
                              setFormData(prev => ({ ...prev, description: r.description || '' }));
                              return null;
                            }}
                          />
                        </ErrorBoundary>
                      </div>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      />
                    </div>

                    {/* Progress & Actual Hours (edit-only fields) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Actual Hours</label>
                        <input
                          type="number"
                          name="actual_hours"
                          value={formData.actual_hours || ''}
                          onChange={handleChange}
                          step="0.5"
                          min="0"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Progress <span className="text-blue-600 font-bold">{formData.progress_percent ?? 0}%</span>
                        </label>
                        <input
                          type="range"
                          name="progress_percent"
                          value={formData.progress_percent ?? 0}
                          onChange={handleChange}
                          min="0"
                          max="100"
                          className="w-full mt-2"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveSection('details')}
                        disabled={!isBasicValid}
                        className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-1.5 shadow-md"
                      >
                        Next: Details
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Details Section */}
                {activeSection === 'details' && (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    {/* Row 1: Task Type | Priority | Repeat this task */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Task Type</label>
                        <div className="relative">
                          <div
                            onClick={() => setShowTypeDropdown(prev => !prev)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg cursor-pointer flex justify-between items-center bg-white"
                          >
                            <span className={formData.task_type ? "text-gray-900" : "text-gray-400"}>
                              {formData.task_type || "Select Type"}
                            </span>
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>

                          {showTypeDropdown && (
                            <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto py-1" style={{ zIndex: 200 }}>
                              {taskTypes.map((type) => {
                                const isSelected = formData.task_type === type.name;
                                return (
                                  <div
                                    key={type.id}
                                    className={`flex items-center justify-between px-3 py-1.5 cursor-pointer group transition-colors ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'}`}
                                    onClick={() => {
                                      setFormData({ ...formData, task_type: type.name });
                                      setShowTypeDropdown(false);
                                    }}
                                  >
                                    <span className="flex-1 text-sm">{type.name}</span>
                                    {!type.is_default && (
                                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditType(type);
                                          }}
                                          className="text-blue-500 hover:text-blue-700 text-xs px-1 rounded"
                                          title="Edit"
                                        >
                                          ✏️
                                        </button>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteType(type);
                                          }}
                                          className="text-red-500 hover:text-red-700 text-xs px-1 rounded"
                                          title="Delete"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    )}
                                    {type.is_default && (
                                      <span className="text-[10px] uppercase tracking-wide text-gray-400 ml-2">Default</span>
                                    )}
                                  </div>
                                );
                              })}

                              <div
                                onClick={() => {
                                  setShowCreateTaskTypeModal(true);
                                  setShowTypeDropdown(false);
                                }}
                                className="px-3 py-1.5 mt-1 text-sm text-blue-600 font-medium hover:bg-blue-50 cursor-pointer border-t border-gray-100 transition-colors"
                              >
                                + Create New Type
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Priority</label>
                        <Select
                          value={formData.priority}
                          onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v as any }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Priority" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LOW">🟢 Low</SelectItem>
                            <SelectItem value="MEDIUM">🟡 Medium</SelectItem>
                            <SelectItem value="HIGH">🟠 High</SelectItem>
                            <SelectItem value="URGENT">🔴 Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Repeat this task — compact toggle in row 1; expanded
                          schedule options render full-width below the row. */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Repeat this task</label>
                        <label className="w-full px-4 py-2.5 border border-gray-300 rounded-lg cursor-pointer flex items-center gap-2 bg-white">
                          <input
                            type="checkbox"
                            checked={!!formData.is_recurring}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, is_recurring: e.target.checked }))
                            }
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-700">
                            {formData.is_recurring ? 'Recurring' : 'One-time task'}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Recurrence options — only when "Repeat this task" is on */}
                    {formData.is_recurring && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50/70 px-3.5 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm text-gray-600">Every</span>
                          <input
                            type="number"
                            min={1}
                            value={formData.recurrence_every ?? 1}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                recurrence_every: Math.max(1, parseInt(e.target.value || '1', 10)),
                              }))
                            }
                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded bg-white"
                          />
                          <div className="w-36">
                            <Select
                              value={formData.recurrence_interval || 'WEEKLY'}
                              onValueChange={(v) =>
                                setFormData((prev) => ({ ...prev, recurrence_interval: v as RecurrenceInterval }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="DAILY">Daily</SelectItem>
                                <SelectItem value="WEEKLY">Weekly</SelectItem>
                                <SelectItem value="MONTHLY">Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <span className="w-full text-xs text-gray-500">
                            A copy will be created every {formData.recurrence_every ?? 1}{' '}
                            {intervalUnit(formData.recurrence_interval || 'WEEKLY', formData.recurrence_every ?? 1)}, starting{' '}
                            {format(
                              new Date(
                                computeNextSpawnAt(
                                  formData.recurrence_interval || 'WEEKLY',
                                  formData.recurrence_every ?? 1,
                                ),
                              ),
                              'MMM dd, yyyy',
                            )}
                            .
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Row 2: Start Date | Due Date | Estimated Hours */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Start (Task) Date — when this task is planned to
                          be worked on. Drives the due_date stamped on
                          every checklist-derived Todo in the Daily
                          Planner, so editing it back-fills the planner
                          via the server's _resync_planner_to_task_date
                          helper. Simple date input (no time) since
                          start_date is a date-only field on Task. */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Start Date
                        </label>
                        <input
                          type="date"
                          name="start_date"
                          value={formData.start_date || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              start_date: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>

                      {/* Due Date with Calendar Picker */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Due Date
                        </label>
                        
                        <button
                          type="button"
                          onClick={() => setDueOpen(true)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                        >
                          <span className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4 text-gray-500" />
                            <span className={dueDate ? "text-gray-900" : "text-gray-400"}>
                              {dueDate ? format(dueDate, "MMM dd, yyyy HH:mm") : "Pick due date"}
                            </span>
                          </span>
                        </button>

                        {dueOpen && createPortal(
                          <div 
                            className="fixed inset-0 flex items-center justify-center bg-black/40"
                            style={{ zIndex: 120 }}
                            onClick={() => setDueOpen(false)}
                          >
                            <div 
                              className="bg-white rounded-md shadow-md w-[260px]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Calendar
                                mode="single"
                                selected={dueDate || undefined}
                                onSelect={handleDateSelect}
                                className="p-2 [&_.rdp-caption]:mb-0 [&_.rdp-caption_label]:text-[11px] [&_.rdp-nav_button]:w-5 [&_.rdp-nav_button]:h-5 [&_.rdp-day]:w-6 [&_.rdp-day]:h-6 [&_.rdp-day]:text-[11px] [&_.rdp-day_button]:w-6 [&_.rdp-day_button]:h-6 [&_.rdp-head_cell]:w-6 [&_.rdp-head_cell]:text-[10px]"
                              />
                              
                              <div className="flex gap-1 p-2 pt-0 border-t border-gray-100">
                                <input
                                  type="time"
                                  value={dueTime}
                                  onChange={handleTimeChange}
                                  className="flex-1 px-1 py-0.5 text-[11px] border border-gray-300 rounded"
                                />
                                <button
                                  onClick={() => {
                                    setDueDate(null);
                                    setDueTime("");
                                    setFormData((prev) => ({ ...prev, due_date: "" }));
                                    setDueOpen(false);
                                  }}
                                  className="px-2 py-0.5 text-[11px] text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                                >
                                  Clear
                                </button>
                                <button
                                  onClick={() => setDueOpen(false)}
                                  className="px-2 py-0.5 text-[11px] bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                  OK
                                </button>
                              </div>
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>

                      {/* Estimated Hours — completes row 2 */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Estimated Hours</label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            aria-label="Estimated hours"
                            type="number"
                            name="estimated_hours"
                            value={estimatedWorkloadParts.hours}
                            onChange={(e) => setEstimatedWorkload(e.target.value, estimatedWorkloadParts.minutes)}
                            step="1"
                            min="0"
                            placeholder="Hours"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                          />
                          <input
                            aria-label="Estimated minutes"
                            type="number"
                            value={estimatedWorkloadParts.minutes}
                            onChange={(e) => setEstimatedWorkload(estimatedWorkloadParts.hours, e.target.value)}
                            step="15"
                            min="0"
                            max="59"
                            placeholder="Min"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Project | Client | Vendor */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Project Dropdown */}
                      <div>
                        <div className="flex justify-between items-center mb-1.5">
                          <label className="block text-sm font-semibold text-gray-700">Project <span className="text-gray-400 font-normal">(optional)</span></label>
                        </div>
                        <EntityMultiSelect
                          values={selectedProjectIds}
                          onChange={(ids) => {
                            const nextIds = compactUniqueIds(ids);
                            // Soft-fill the client list from the picked
                            // projects only while it is still empty; an
                            // explicit choice is never widened.
                            const derived = compactUniqueIds(
                              nextIds.map((id) => projects.find((p) => p.id === id)?.client_id),
                            );
                            const nextClientIds = selectedClientIds.length ? selectedClientIds : derived;
                            setFormData((prev) => ({
                              ...prev,
                              project_id: nextIds[0] || null,
                              project_ids: nextIds,
                              client_id: nextClientIds[0] || null,
                              client_ids: nextClientIds,
                            } as any));
                          }}
                          options={projects
                            .filter(p => selectedClientIds.length === 0 || !p.client_id || selectedClientIds.includes(p.client_id))
                            .map((p) => ({ id: p.id, label: p.name, meta: p.client_name || null }))}
                          placeholder="Select Projects"
                          singularLabel="project"
                          pluralLabel="projects"
                          searchPlaceholder="Search projects..."
                          emptyText="No projects available."
                          addLabel="+ Add Project"
                          onAddNew={() => {
                            setNewProjectForm(f => ({ ...f, client_id: selectedClientIds[0] || '' }));
                            setShowAddProjectModal(true);
                          }}
                        />
                      </div>

                      {/* Client picker with Add Client Modal */}
                      <div>
                        <div className="flex justify-between items-center mb-1.5">
                          <label className="block text-sm font-semibold text-gray-700">Client <span className="text-gray-400 font-normal">(optional)</span></label>
                        </div>
                        <EntityMultiSelect
                          values={selectedClientIds}
                          onChange={(ids) => {
                            const nextIds = compactUniqueIds(ids);
                            setFormData((prev) => ({
                              ...prev,
                              client_id: nextIds[0] || null,
                              client_ids: nextIds,
                            } as any));
                          }}
                          options={clients.map((c) => ({
                            id: c.id,
                            label: c.clientName,
                            meta: (c as any).email || (c as any).phone || null,
                          }))}
                          placeholder="Select Clients"
                          singularLabel="client"
                          pluralLabel="clients"
                          searchPlaceholder="Search clients..."
                          emptyText="No clients available."
                          addLabel="+ Add Client"
                          onAddNew={() => setShowAddClientModal(true)}
                        />
                      </div>

                      {/* Vendor picker */}
                      <div>
                        <div className="flex justify-between items-center mb-1.5">
                          <label className="block text-sm font-semibold text-gray-700">Vendor <span className="text-gray-400 font-normal">(optional)</span></label>
                        </div>
                        <EntityMultiSelect
                          values={selectedVendorIds}
                          onChange={(ids) => {
                            const nextIds = compactUniqueIds(ids);
                            setFormData((prev) => ({
                              ...prev,
                              vendor_id: nextIds[0] || null,
                              vendor_ids: nextIds,
                            } as any));
                          }}
                          options={vendors.map((v) => ({
                            id: v.id,
                            label: v.clientName,
                            meta: (v as any).email || (v as any).phone || null,
                          }))}
                          placeholder="Select Vendors"
                          singularLabel="vendor"
                          pluralLabel="vendors"
                          searchPlaceholder="Search vendors..."
                          emptyText="No vendors available."
                        />
                      </div>
                    </div>

                    <div className="flex justify-between pt-2">
                      <button
                        type="button"
                        onClick={() => setActiveSection('basic')}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection(hideAssignmentControls ? 'checklist' : 'approval')}
                        className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-1.5 shadow-md"
                      >
                        {hideAssignmentControls ? 'Next: Checklist' : 'Next: Approval Chain'}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Approval Chain Section */}
                {!hideAssignmentControls && activeSection === 'approval' && (
                  <div className="space-y-2 animate-in fade-in duration-300">
                    {/* Two columns on desktop (stacks on mobile) so the section
                        stays compact: Preparors on the left, Approval Chain on
                        the right. items-stretch keeps both cards the same height. */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
                    {/* Preparors — one checkbox multi-select (search + Select All)
                        instead of adding rows one at a time. */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-200 flex flex-col">
                      <div className="mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">1</div>
                          <h3 className="font-semibold text-gray-900">Add Preparors (Workers)</h3>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Optionally add more people who will do the work</p>
                      </div>

                      <EmployeeMultiSelect
                        values={selectedPreparors.filter(Boolean)}
                        onChange={(ids) => setSelectedPreparors(ids)}
                        options={loadingUsers ? [] : assignableUsers}
                        disabled={loadingUsers}
                        placeholder={loadingUsers ? "Loading users…" : "Select preparors"}
                      />

                      <TaskWorkloadPreviewList
                        employeeIds={capacityEmployeeIds}
                        previews={workloadPreviews}
                        requestedHours={requestedWorkloadHours}
                        getEmployeeName={getUserDisplayName}
                      />

                      {selectedPreparors.filter(Boolean).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {selectedPreparors.filter(Boolean).map((id) => (
                            <span key={id} className="inline-flex items-center gap-1 pl-0.5 pr-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                              <PersonAvatar userId={id} name={getUserDisplayName(id)} size={20} />
                              {getUserDisplayName(id)}
                              <button
                                type="button"
                                onClick={() => setSelectedPreparors(selectedPreparors.filter((p) => p !== id))}
                                className="hover:text-blue-900"
                                aria-label="Remove preparor"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="mt-2 text-xs text-gray-500">
                        <span className="font-medium">{selectedPreparors.filter(p => p).length}</span> preparor(s) selected
                      </div>
                    </div>

                    {/* Approval Chain — one reviewer multi-select; checked people
                        become approval levels in the order picked. */}
                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <div className="mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">2</div>
                          <h3 className="font-semibold text-gray-900">Approval Chain</h3>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Pick reviewers — they approve in the order selected</p>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">Reviewers</label>
                        <EmployeeMultiSelect
                          values={(formData.approvalChain || []).map((l) => l.approver).filter(Boolean)}
                          onChange={setReviewers}
                          options={loadingUsers ? [] : assignableUsers}
                          disabled={loadingUsers}
                          placeholder={loadingUsers ? "Loading users…" : "Select reviewers"}
                        />
                      </div>

                      {(formData.approvalChain || []).filter((l) => l.approver).length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {(formData.approvalChain || []).filter((l) => l.approver).map((level) => (
                            <div key={level.approver} className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 border border-gray-200 text-sm">
                              <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 text-xs font-bold flex items-center justify-center shrink-0">
                                {level.level}
                              </div>
                              <PersonAvatar userId={level.approver} name={getUserDisplayName(level.approver)} size={20} />
                              <span className="flex-1 min-w-0 truncate text-gray-700">{getUserDisplayName(level.approver)}</span>
                              <span className="text-xs text-gray-400 shrink-0">{level.roleLabel}</span>
                              <button
                                type="button"
                                onClick={() => setReviewers((formData.approvalChain || []).map((l) => l.approver).filter((a) => a && a !== level.approver))}
                                className="text-red-500 hover:text-red-700 shrink-0"
                                aria-label="Remove reviewer"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    </div>

                    <div className="flex justify-between pt-3">
                      <button
                        type="button"
                        onClick={() => setActiveSection('details')}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection('checklist')}
                        className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-1.5 shadow-md"
                      >
                        Next: Checklist
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Checklist Section */}
                {activeSection === 'checklist' && (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    <div>
                      <div className="flex justify-between items-end mb-2 gap-3">
                        <label className="block text-sm font-semibold text-gray-700">Checklist Items</label>
                        <div className="flex items-center gap-3">
                          <ErrorBoundary inline name="ai-generate-checklist">
                            <AiGenerateButton
                              idleLabel="Generate with AI"
                              disabled={!(formData.title || '').trim() && !(formData.description || '').trim()}
                              disabledHint="Add a title or description first"
                              run={async () => {
                                const r = await generateTaskChecklist(formData.title || '', formData.description || '');
                                if (!r.ok) return r.message || 'AI is unavailable right now.';
                                const generated = (r.items || []).map(s => s.trim()).filter(Boolean);
                                if (!generated.length) return 'No checklist items generated. Try again.';
                                setChecklistItems(prev => {
                                  const existing = (prev || []).filter(s => s.trim());
                                  return existing.length ? [...existing, ...generated] : generated;
                                });
                                return null;
                              }}
                            />
                          </ErrorBoundary>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {checklistItems.filter(i => i.trim()).length} item(s)
                          </span>
                        </div>
                      </div>
                      {checklistItems.length === 0 && (
                        <p className="text-sm text-gray-400 italic mb-2">No checklist items yet. Click below to add.</p>
                      )}
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                        {checklistItems.map((item, index) => (
                          <div key={index} className="flex gap-2 items-start group">
                            <div className="w-6 h-6 mt-1.5 shrink-0 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                              {index + 1}
                            </div>
                            <AutoGrowTextarea
                              value={item}
                              onChange={e => handleChecklistChange(index, e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) e.preventDefault(); }}
                              placeholder={`Task item ${index + 1}`}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            />
                            <button
                              type="button"
                              onClick={() => removeChecklistItem(index)}
                              className="opacity-0 group-hover:opacity-100 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addChecklistItem}
                        className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Checklist Item
                      </button>
                    </div>

                    {/* Summary */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                      <h4 className="font-semibold text-gray-800 text-sm mb-2">📋 Update Summary</h4>
                      <div className="space-y-1 text-xs text-gray-600">
                        <p><strong>Title:</strong> {formData.title || 'Not set'}</p>
                        <p><strong>Priority:</strong> {formData.priority || 'Not set'}</p>
                        <p><strong>Preparors:</strong> {selectedPreparors.filter(p => p).length > 0 ? selectedPreparors.filter(p => p).map(p => getUserDisplayName(p)).join(', ') : 'None'}</p>
                        <p><strong>Reviewers:</strong> {(formData.approvalChain || []).filter(l => l.approver).length}</p>
                        <p><strong>Checklist Items:</strong> {checklistItems.filter(i => i.trim()).length}</p>
                      </div>
                    </div>

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveSection('approval')}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection('attachments')}
                        className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-1.5 shadow-md"
                      >
                        Next: Attachments
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Attachments Section — task already exists, so we upload
                    files immediately against task.id (same pattern as the
                    TaskDetails attachments tab). */}
                {activeSection === 'attachments' && (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    <div
                      ref={attachmentDropRef}
                      tabIndex={-1}
                      className={`outline-none rounded-lg transition-all ${
                        attachmentDragOver ? 'ring-2 ring-blue-400 ring-offset-2 bg-blue-50/40 p-3' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Attachments</label>
                        <span className="text-xs text-gray-400">
                          {attachments.length} file{attachments.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        Drag &amp; drop, paste with Ctrl/Cmd+V, or click below. Files upload immediately. Max 5 MB per file.
                      </p>

                      <input
                        ref={attachmentInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (e.target) e.target.value = '';
                          await uploadAttachmentFiles(files);
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => attachmentInputRef.current?.click()}
                        disabled={uploadingAttachments}
                        className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploadingAttachments ? (
                          <>
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Uploading…
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Attachment
                          </>
                        )}
                      </button>

                      {loadingAttachments ? (
                        <p className="mt-4 text-xs text-gray-500 italic">Loading attachments…</p>
                      ) : attachments.length === 0 ? (
                        <p className="mt-4 text-xs text-gray-500 italic">No attachments yet.</p>
                      ) : (
                        <ul className="mt-4 space-y-2">
                          {attachments.map((a) => (
                            <li
                              key={a.id}
                              className="flex items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                {/* Image thumbnail / PDF badge / generic
                                    file icon — matches the DocUploadCard
                                    pattern in Employee form. The thumb
                                    itself is a link to the uploaded URL
                                    so users can click to open. */}
                                <AttachmentThumb
                                  url={a.file_url ?? null}
                                  mime={a.mime_type ?? null}
                                  name={a.file_name}
                                />
                                <div className="min-w-0">
                                  {a.file_url ? (
                                    <a
                                      href={a.file_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-sm text-blue-600 hover:underline truncate block"
                                    >
                                      {a.file_name}
                                    </a>
                                  ) : (
                                    <p className="text-sm text-gray-800 truncate">{a.file_name}</p>
                                  )}
                                  {a.file_size != null && (
                                    <p className="text-[11px] text-gray-500">{(a.file_size / 1024).toFixed(1)} KB</p>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await tasksApi.deleteAttachment(a.id);
                                    await loadAttachments();
                                    showSuccess('Attachment deleted', a.file_name);
                                  } catch (err: any) {
                                    showError('DELETE_FAILED', err?.response?.data?.detail || err?.message || 'Could not delete');
                                  }
                                }}
                                className="text-red-600 hover:text-red-700 text-xs font-medium"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveSection('checklist')}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading || !isBasicValid || hasCapacityBlockers}
                          className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-1.5 shadow-md"
                        >
                          {loading ? (
                            <>
                              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Saving...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Save Changes
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Create Task Type Modal */}
      {showCreateTaskTypeModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110]"
            onClick={() => setShowCreateTaskTypeModal(false)}
          />
          <div className="fixed inset-0 z-[110] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-300"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l5 5a2 2 0 01.586 1.414V19a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Create Task Type</h3>
                    </div>
                    <button onClick={() => setShowCreateTaskTypeModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Type Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTaskTypeName}
                        onChange={e => setNewTaskTypeName(e.target.value)}
                        placeholder="e.g., Documentation, Research, Testing"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        autoFocus
                        onKeyPress={e => { if (e.key === 'Enter') handleCreateTaskType(); }}
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowCreateTaskTypeModal(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateTaskType}
                        disabled={creatingTaskType || !newTaskTypeName.trim()}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 font-medium flex items-center justify-center gap-2 shadow-md"
                      >
                        {creatingTaskType ? (
                          <>
                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Creating...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Create Type
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Task Type Modal */}
      {showEditModal && editingType && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110]"
            onClick={() => setShowEditModal(false)}
          />
          <div className="fixed inset-0 z-[110] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-300">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Edit Task Type</h3>
                    <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg mb-4"
                    autoFocus
                  />
                  <div className="flex gap-3">
                    <button onClick={() => setShowEditModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                    <button onClick={handleUpdateTaskType} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg">Update</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteType && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110]"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="fixed inset-0 z-[110] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-300">
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Task Type</h3>
                  <p className="text-gray-700 mb-4">Are you sure you want to delete "{deleteType.name}"?</p>
                  <div className="flex gap-3">
                    <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                    <button onClick={handleDeleteTaskType} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Quick Add Client Modal - Device Friendly */}
      {showAddClientModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={() => setShowAddClientModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 p-5 border-b border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0M12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Add New Client</h3>
                <p className="text-xs text-gray-500">Quick add — full details in Client panel</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name <span className="text-red-500">*</span></label>
                <input type="text" value={newClientForm.clientName} onChange={e => setNewClientForm(f => ({ ...f, clientName: e.target.value }))}
                  placeholder="e.g., Acme Corporation" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" autoFocus />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={newClientForm.email} onChange={e => setNewClientForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="email@co.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={newClientForm.phone} onChange={e => setNewClientForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+91..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input type="text" value={newClientForm.address1} onChange={e => setNewClientForm(f => ({ ...f, address1: e.target.value }))}
                  placeholder="Street address" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <input type="text" value={newClientForm.address2} onChange={e => setNewClientForm(f => ({ ...f, address2: e.target.value }))}
                  placeholder="Area, landmark…" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input type="text" value={newClientForm.state} onChange={e => setNewClientForm(f => ({ ...f, state: e.target.value }))}
                    placeholder="State" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input type="text" value={newClientForm.country} onChange={e => setNewClientForm(f => ({ ...f, country: e.target.value }))}
                    placeholder="Country" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input type="text" value={newClientForm.pincode} onChange={e => setNewClientForm(f => ({ ...f, pincode: e.target.value }))}
                    placeholder="000000" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                  <input type="text" value={newClientForm.gstin} onChange={e => setNewClientForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))}
                    placeholder="22AAAAA0000A1Z5" maxLength={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                  <input type="text" value={newClientForm.pan} onChange={e => setNewClientForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))}
                    placeholder="AAAAA0000A" maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-mono" />
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white p-5 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setShowAddClientModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
              <button onClick={handleSaveNewClient} disabled={savingClient || !newClientForm.clientName.trim()}
                className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all text-sm font-medium disabled:opacity-50">
                {savingClient ? 'Adding…' : 'Add Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Project Modal */}
      {showAddProjectModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={() => setShowAddProjectModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Add New Project</h3>
                <p className="text-xs text-gray-500">Quick add — manage details in Projects panel</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name <span className="text-red-500">*</span></label>
                <input type="text" value={newProjectForm.name} onChange={e => setNewProjectForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Website Redesign" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client (optional)</label>
                <Select
                  value={newProjectForm.client_id || "__none__"}
                  onValueChange={(v) => setNewProjectForm(f => ({ ...f, client_id: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— No client —</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.clientName}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={newProjectForm.description} onChange={e => setNewProjectForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} placeholder="Brief description..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setShowAddProjectModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
              <button onClick={handleSaveNewProject} disabled={savingProject || !newProjectForm.name.trim()}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all text-sm font-medium disabled:opacity-50">
                {savingProject ? 'Adding…' : 'Add Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes zoomIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-in { animation-duration: 0.3s; animation-fill-mode: both; }
        .fade-in { animation-name: fadeIn; }
        .zoom-in { animation-name: zoomIn; }
      `}</style>
    </div>,
    document.body
  );
}

export default TaskEdit;
